import type { LeadContactInfo, LeadRecord } from "./types.js";
import type { ProjectType } from "@reno-estimator/core";

/**
 * Two independent layers of duplicate-submission protection, since they
 * catch different real-world situations:
 *
 * 1. `SubmissionGuard` - a same-session, in-flight guard against a double
 *    click on the submit button firing two network calls for one intended
 *    submission.
 * 2. `buildLeadFingerprint`/`findRecentDuplicate` - a same-contact,
 *    same-project check against already-stored leads, catching a form
 *    resubmitted after a page reload, a back-button retry, or the same
 *    person filling the form twice in two different sessions.
 */

/** Normalizes contact + project into a stable key so the "same lead" can be recognized across sessions regardless of minor formatting differences (casing, spacing). */
export function buildLeadFingerprint(contact: LeadContactInfo, projectType: ProjectType): string {
  const email = contact.email?.trim().toLowerCase() ?? "";
  const phone = contact.phone?.replace(/\D/g, "") ?? "";
  return `${projectType}:${email}:${phone}`;
}

export interface DuplicateCheckOptions {
  /** How long a fingerprint is considered a duplicate after its first submission. Default 24 hours. */
  windowMs?: number;
  now?: () => Date;
}

const DEFAULT_DUPLICATE_WINDOW_MS = 24 * 60 * 60 * 1000;

/** Finds the most recent existing lead that matches the given fingerprint within the configured time window, or undefined if this is not a duplicate. */
export function findRecentDuplicate(existingLeads: LeadRecord[], fingerprint: string, options: DuplicateCheckOptions = {}): LeadRecord | undefined {
  const windowMs = options.windowMs ?? DEFAULT_DUPLICATE_WINDOW_MS;
  const now = (options.now ?? (() => new Date()))();

  const matches = existingLeads
    .filter((lead) => lead.dedupeFingerprint === fingerprint)
    .filter((lead) => now.getTime() - Date.parse(lead.createdAt) <= windowMs)
    .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));

  return matches[0];
}

/**
 * Simple reentrancy guard for the UI layer: `start()` returns true and
 * marks a submission in-flight, or returns false if one is already
 * running (e.g. a double click). Callers must call `finish()` when the
 * submission settles (success or failure) to release the guard.
 */
export class SubmissionGuard {
  private inFlight = false;

  start(): boolean {
    if (this.inFlight) return false;
    this.inFlight = true;
    return true;
  }

  finish(): void {
    this.inFlight = false;
  }

  get isInFlight(): boolean {
    return this.inFlight;
  }
}
